import React, { useState } from 'react';
import { OpportunityEntity } from 'shared/types/domain/OpportunityEntity';
import { ApolloError } from 'apollo-client';
import { FormFieldData, FormProvider } from 'modules/forms';
import { ObservationFileEntity } from 'shared/types/file/ObservationFileEntity';
import { useFiles } from 'shared/utils/files/hooks/useFiles';
import { allowedMimeTypes } from 'shared/options/observation-files.options';
import { mapFileToFormData } from 'shared/utils/mapping.utils';
import { useRestClient } from 'modules/rest-client/hooks/useRestClient';
import { IFileUploadResponse } from 'shared/types/file/IFileUploadRequest';
import { ErrorListToastMessage } from 'modules/errors';
import { useToasts } from 'react-toast-notifications';
import { useApolloClient } from '@apollo/react-hooks';
import { IAppConfig, useConfig } from 'modules/config';
import { IFormIsValidFunction } from 'modules/forms/types/IFormIsValidFunction';
import { ActionResponse } from 'modules/errors/classes/ActionResponse';
import Loading from 'components/layout/Loading';
import GraphQlErrorBoundary from 'components/layout/ErrorBoundary';
import { StyledNearMissContainer } from 'sections/near-miss-management/pages/ViewAmmendNearMiss/styled/ViewAmmendNearMiss.styled';
import { isNullOrUndefined } from 'shared/utils/validation.utils';
import { createOpportunityOptions } from '../options/create-opportunity.options';
import { CreateOpportunityResponse, CreateOpportunityVariables } from '../mutations/create-opportunity.mutations.types';
import { CreateOpportunity } from '../mutations/create-opportunity.mutation';
import { CreateOpportunityRequestMapper } from '../mappers/CreateOpportunityMapper';
import NewOpportunityHeader from './NewOpportunityHeader';
import NewOpportunitySubHeader from './OpportunitySubHeader';
import NewOpportunityForm from './NewOpportunityForm';
import { NewOpportunityLoadingState } from '../enums/new-near-miss-loading-state.enum';

interface NewNearMissMainProps {
   onComplete: () => void;
}

export const NewOpportunityMain = ({ onComplete }: NewNearMissMainProps) => {
   const { addToast } = useToasts();
   const [opportunityForm] = useState<FormFieldData<OpportunityEntity> | undefined>();
   const [isLoading, setIsLoading] = useState<boolean>(false);
   const [loadingMessage, setLoadingMessage] = useState<string>(
      NewOpportunityLoadingState.loadingOpportunity
   );
   const [loadingError] = useState<ApolloError>();

   const { settings } = useConfig<IAppConfig>();
   const restClient = useRestClient<'fileUpload'>();
   const setLoadingState = (loadingMessage: NewOpportunityLoadingState, isLoading: boolean): void => {
      setLoadingMessage(loadingMessage);
      setIsLoading(isLoading);
   };

   const client = useApolloClient();

   const { files, addFiles, clearFiles, syncFiles, removeFilesByIds } = useFiles<ObservationFileEntity>({
      allowedMimeTypes,
      onNotAllowed: handleNotAllowedFilesAdded,
      addFile: uploadFile,
      updateFile: () => { },
      removeFile: () => { },
      maxFileUploadBytes: settings.MaxFileUploadBytes
   });

   function handleNotAllowedFilesAdded(messages: string[]): void {
      addToast(
         <ErrorListToastMessage
            errors={messages}
            baseMessage={"There were disallowed file types in the collection of files uploaded"}
         />,
         { appearance: 'warning' }
      );
   }

   async function uploadFile(
      file: ObservationFileEntity,
      folderName?: string,
      data?: UID
   ): Promise<ObservationFileEntity> {
      const formData = mapFileToFormData<{ referenceId: UID | undefined }>(file, settings, folderName, {
         referenceId: data
      });

      const endpoint = restClient.getEndpoint('fileUpload');
      const result = await endpoint.post<IFileUploadResponse>({
         method: 'UploadFile',
         data: formData,
         onUploadProgress: p => { }
      });

      return { ...file, fileId: result.data.fileId };
   }

   const handleSaveChanges = async (
      formData: FormFieldData<OpportunityEntity> | undefined,
      fileData: ObservationFileEntity[] | undefined,
      isValid: IFormIsValidFunction
   ): Promise<ActionResponse> => {
      setLoadingState(NewOpportunityLoadingState.submitting, true);

      const response = await createOpportunityEntity(formData, isValid);

      setLoadingState(NewOpportunityLoadingState.submitting, false);

      if (!response.successful) {
         addToast(<ErrorListToastMessage errors={[response.error.message]} />, { appearance: 'error' });

         return response;
      }

      if (files) {
         setLoadingState(NewOpportunityLoadingState.uploadingFiles, true);

         const typedData = response.data as CreateOpportunityResponse;
         const filesSynced = await syncFiles(typedData.observationId.toString(), typedData.uniqueRecordId);

         setLoadingState(NewOpportunityLoadingState.uploadingFiles, false);

         if (!filesSynced) {
            addToast(<ErrorListToastMessage errors={["There was an issue uploading files"]} />, { appearance: 'error' });

            clearFiles();

            return response;
         }
      }

      addToast('Changes saved successfully', { appearance: 'success' });

      onComplete();

      return response;
   };

   const createOpportunityEntity = async (
      formData: FormFieldData<OpportunityEntity> | undefined,
      isValid: IFormIsValidFunction
   ): Promise<any> => {
      let actionResponse = {};

      if (isNullOrUndefined(formData) || !isValid(true)) {
         return {
            successful: false,
            error: {
               message: 'Some of the information entered was not valid, please fix any validation errors before continuing'
            }
         };
      }

      const mapper = new CreateOpportunityRequestMapper();
      const requestObject = mapper.map(formData);

      try {
         const response = await client.mutate<{ createOpportunity: CreateOpportunityResponse }, CreateOpportunityVariables>({
            mutation: CreateOpportunity,
            variables: {
               request: requestObject
            }
         });

         actionResponse = { successful: true, data: response.data?.createOpportunity };
      } catch (error) {
         actionResponse = {
            successful: false,
            error: {
               message: 'There was an issue creating the near miss record',
               error: error
            }
         };
      }
      return actionResponse;
   }

   return (
      <React.Fragment>
         <Loading isLoading={isLoading} message={loadingMessage} noDelay>
            <GraphQlErrorBoundary error={loadingError}>
               <StyledNearMissContainer>
                  <FormProvider<OpportunityEntity> 
                     options={createOpportunityOptions} 
                     formFields={opportunityForm}
                     useEnglish
                  >
                     <NewOpportunityHeader onSave={handleSaveChanges} isLoading={isLoading} files={files} />
                     <NewOpportunitySubHeader formHasErrors={false} goToOverview={() => { }} />
                     <NewOpportunityForm handleAddFile={addFiles} handleDeleteFile={removeFilesByIds} />
                  </FormProvider>
               </StyledNearMissContainer>
            </GraphQlErrorBoundary>
         </Loading>
      </React.Fragment>
   );
};
